import { decodeJWT, LOGIN_STORAGE_KEY, PALZIN_TOKEN, whichEnv } from "Utils";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { useUserLogged } from "hooks/Login/useLogin";
import { ApiResponse } from "types/OnDemand.types";
const servicConfigs = require("./serviceConfig.json");

export const ServiceProvider = {
  BigDataCorp: "BigDataCorp",
  NinProxy: "NinProxy",
  NinApi: "NinApi",
  Palzin: "Palzin",
} as const;

export type ServiceProvider =
  (typeof ServiceProvider)[keyof typeof ServiceProvider];

export const HttpClient = (
  api: ServiceProvider = ServiceProvider.BigDataCorp,
): AxiosInstance => {
  const serviceConfig = servicConfigs[whichEnv()];
  const baseURL = serviceConfig[api].url;
  const timeout = serviceConfig[api].timeout
    ? serviceConfig[api].timeout
    : 10000;
  switch (api) {
    case ServiceProvider.NinApi:
      const ninApi = axios.create({
        baseURL,
        timeout: 10000 * 120,
      });
      addRequestInterceptorNinApi(ninApi);
      addResponseInterceptor(ninApi);
      return ninApi;
    case ServiceProvider.NinProxy:
      return axios.create({
        baseURL,
        timeout: serviceConfig[api].timeout,
      });
    case ServiceProvider.Palzin:
      const palzinClient = axios.create({
        baseURL,
      });
      addRequestInterceptorPalzin(palzinClient);
      return palzinClient;
    case ServiceProvider.BigDataCorp:
      const bigDataCorpClient = axios.create({
        baseURL,
        timeout: 10000 * 120,
      });
      addRequestInterceptor(bigDataCorpClient);
      addResponseInterceptor(bigDataCorpClient);
      return bigDataCorpClient;
  }
};

const addRequestInterceptorPalzin = (client) => {
  client.interceptors.request.use((config: AxiosRequestConfig) => {
    config.headers["Content-Type"] = "application/json";
    config.headers["Authorization"] = `Bearer ${PALZIN_TOKEN}`;
    return { ...config };
  });
};

const addRequestInterceptor = (client) => {
  client.interceptors.request.use((config: AxiosRequestConfig) => {
    const loginInfo = useUserLogged();
    if (loginInfo) {
      const preConfig = useBigDataCorpConfig({
        token: loginInfo.token,
        tokenID: loginInfo.tokenID,
      });
      return { ...config, ...preConfig };
    }
    return { ...config };
  });
};

const addRequestInterceptorNinApi = (client) => {
  client.interceptors.request.use((config: AxiosRequestConfig) => {
    const loginInfo = useUserLogged();
    if (loginInfo) {
      const preConfig = useNinApiConfig({
        token: loginInfo.token,
        tokenID: loginInfo.tokenID,
      });
      return { ...config, ...preConfig };
    }
    return { ...config };
  });
};

const addResponseInterceptor = (client) => {
  client.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      if (response.data.Status?.login?.pop().Message.includes("INVALID")) {
        localStorage.removeItem(LOGIN_STORAGE_KEY);
        throw new Error("INVALID CREDENTIALS");
      }
      return response;
    },
    (error) => {
      if (error?.status == 401) {
        localStorage.removeItem(LOGIN_STORAGE_KEY);
        throw new Error("INVALID CREDENTIALS");
      }
      throw new Error(error);
    },
  );
};

export const useNinApiConfig = ({
  token,
  tokenID,
}: {
  token: string;
  tokenID: string;
}): AxiosRequestConfig => {
  const config: AxiosRequestConfig = {
    headers: {
      accept: "application/json",
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };
  return config;
};

export const useBigDataCorpConfig = ({
  token,
  tokenID,
}: {
  token: string;
  tokenID: string;
}): AxiosRequestConfig => {
  const decoded = decodeJWT(token);
  const config: AxiosRequestConfig = {
    headers: {
      accept: "application/json",
      "content-type": "application/json",
      AccessToken: decoded.payload,
      TokenId: tokenID,
    },
  };
  return config;
};

export const useSerproConfig = (): AxiosRequestConfig => {
  const config: AxiosRequestConfig = {
    headers: {},
  };
  return config;
};
